package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

object BoxStyles : StyleSheet() {
    val box by style {
        shadowBorder()
        borderRadius(SXMUI.boxRadius.value())
        overflow("hidden")

        // A box owns no outer margin — spacing between stacked boxes belongs to the container
        // (e.g. a Column(gap), a detail grid, or a dashboard Row/cell). Callers that stack
        // boxes are responsible for the gap between them.
    }

    val header by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Stretch)
        justifyContent(JustifyContent.SpaceBetween)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        padding(1.cssRem)

        "h2" style {
            margin(0.px)
            fontSize(1.2.cssRem)
            lineHeight((1.8).cssRem)
            marginRight(1.cssRem)
        }

        "h3" style {
            margin(0.px)
            fontSize(1.cssRem)
            marginRight(1.cssRem)
        }

        "p" style {
            margin(0.px)
        }

        border(0.px, LineStyle.Solid, SXMUI.defaultDivider.value())
        borderWidth(0.px, 0.px, 1.px, 0.px)
    }

    val headerContent by style {
        flexGrow(2)
    }

    val headerInstruction by style {
        marginTop(.5.em)
        fontSize(.8.em)
    }

    val headerActions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        alignSelf(AlignSelf.Start)
        gap(10.px)
    }

    val content by style {
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        padding(1.cssRem)
    }

    val noPaddingContent by style {

    }

    val boxMessage by style {
        textAlign("center")
        fontSize(SXMUI.smallFontSize.value())
        color(SXMUI.subtleTextColor.value())
    }
}

class BoxHeaderConfig {
    var title: String? = null
    var instruction: String? = null
    var titleSize: HeaderSize = HeaderSize.NORMAL

    internal var content: (@Composable () -> Unit)? = null
    internal var actionContent: (@Composable () -> Unit)? = null

    internal val actions = mutableListOf<ButtonConfiguration>()

    fun actionContent(block: @Composable () -> Unit) {
        actionContent = block
    }

    fun content(block: @Composable () -> Unit) {
        content = block
    }

    fun action(block: ButtonConfiguration) {
        actions += block
        actionContent = {
            actions.forEach {
                button(it)
            }
        }
    }

    fun action(block: ButtonConfiguration.() -> Unit) {
        action(ButtonConfiguration().apply(block))
    }
}

enum class HeaderSize {
    NORMAL,
    SMALL
}

class BoxConfig {
    var title: String?
        get() = headerConfig.title
        set(value) {
            headerConfig.title = value
        }

    var paddedContent: Boolean = true
    var open: Boolean? = true
    var onToggle: ((Boolean) -> Unit)? = null

    /**
     * When true, the box fills its parent height using flex layout and the content
     * area becomes scrollable. The box header remains fixed at the top while the
     * content area scrolls independently. Useful for fixed-height layouts where
     * the box needs to constrain its content within a bounded space.
     */
    var scrollable: Boolean = false

    /**
     * When true, the box grows to fill its parent's height using flex layout, without making
     * the content scrollable (unlike [scrollable]). Use this when the box lives in a
     * height-stretched container — e.g. a dashboard grid cell — and should match the height of
     * taller siblings. Has no effect on the outer margin.
     */
    var fill: Boolean = false

    internal val headerConfig = BoxHeaderConfig()

    fun header(builder: (BoxHeaderConfig.() -> Unit)? = null, block: (@Composable () -> Unit)? = null) {
        builder?.let {
            headerConfig.it()
        }
        block?.let {
            headerConfig.content(it)
        }
    }

    /**
     * Enable the box to have an expanding option
     */
    fun enableExpanding(defaultState: Boolean, onToggle: ((Boolean) -> Unit)? = null) {
        this.onToggle = {
            onToggle?.invoke(it)
        }
        open = defaultState
    }

    fun action(block: ButtonConfiguration.() -> Unit) {
        headerConfig.action(block)
    }

    fun action(block: ButtonConfiguration) {
        headerConfig.action(block)
    }

    fun action(title: String, primary: Boolean = false, impl: suspend () -> Unit) {
        headerConfig.action {
            this.title = title
            this.primary = primary
            this.action(impl)
        }
    }
}

/**
 * Add a standard menu to the box.
 */
fun BoxConfig.menu(block: ButtonConfiguration.() -> Unit) {
    this.action {
        this.round = true
        content {
            svgIcon(SVGIcons.dots) {
                size = 20.px
            }
        }
        popupAlign = PopupAlign.Right
        block()
    }
}


/**
 * Just a simple box component.
 */
@Composable
fun box(
    title: String,
    config: BoxConfig.() -> Unit = {},
    content: @Composable (() -> Unit)? = null
) {
    box({
        this.title = title
        config()
    }, content)
}

/**
 * Provides a context for box rendering for Box-specific details.
 */
interface BoxContext

/**
 * Create a box, a simple container with a header and content.
 */
@Composable
fun Box(
    builder: BoxConfig.() -> Unit = {},
    content: @Composable (BoxContext.() -> Unit)? = null
) {
    val config = BoxConfig().apply(builder)
    var expanded by remember { mutableStateOf(config.open ?: true) }

    Div({
        classes(BoxStyles.box)
        // Both scrollable and fill make the box grow to fill a stretched parent via flex;
        // scrollable additionally makes the content area scroll (handled in boxContent).
        if (config.scrollable || config.fill) {
            style {
                flex(1)
                minHeight(0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
            }
        }
    }) {
        boxHeader(config.headerConfig, expanded, config.onToggle?.let { toggler ->
            { newToggleState ->
                expanded = newToggleState
                toggler(newToggleState)
            }
        })
        if (expanded) {
            content?.let {
                boxContent(config.paddedContent, scrollable = config.scrollable, fill = config.fill) {
                    val context = object : BoxContext {}
                    context.it()
                }
            }
        }

    }
}

/**
 * Create a box. New code should use Box to match Compose styling.
 */
@Composable
fun box(
    builder: BoxConfig.() -> Unit = {},
    content: @Composable (() -> Unit)? = null
) = Box(builder) {
    content?.invoke()
}

/**
 * Renders the expand/collapse toggle icon for a box header.
 * Uses an animated chevron that rotates smoothly between states.
 *
 * @param expanded Whether the box is currently expanded
 * @param onToggle Callback invoked with the new expanded state when clicked
 */
@Composable
private fun boxHeaderToggle(expanded: Boolean, onToggle: ((Boolean) -> Unit)) {
    Span({
        style {
            marginRight(6.px)
        }
    }) {
        expandingIcon(expanded) {
            onToggle { onToggle(!expanded) }
        }
    }
}

@Composable
private fun boxHeader(
    config: BoxHeaderConfig, expanded: Boolean, onToggle: ((Boolean) -> Unit)? = null
) {
    if (config.title == null && config.actions.isEmpty() && config.content == null &&
        config.instruction == null &&
        config.actionContent == null && onToggle == null
    ) {
        return // nothing to render
    }

    Div({
        classes(BoxStyles.header)
    }) {
        Div({ classes(BoxStyles.headerContent) }) {
            config.title?.let {
                if (config.titleSize == HeaderSize.SMALL) {
                    H3 {
                        if (onToggle != null) boxHeaderToggle(expanded, onToggle)
                        Text(it)
                    }
                } else {
                    H2 {
                        if (onToggle != null) boxHeaderToggle(expanded, onToggle)
                        Text(it)
                    }

                }
            }

            config.instruction?.let {
                Div({ classes(BoxStyles.headerInstruction) }) {
                    Text(it)
                }
            }

            config.content?.let {
                Div({
                    style {
                        if (config.title != null) {
                            marginTop((0.5).cssRem)
                        }
                    }
                }) {
                    it()
                }
            }
        }
        Div({ classes(BoxStyles.headerActions) }) {
            config.actionContent?.invoke()
        }
    }
}

/**
 * Renders the content area of a box. When [scrollable] is true, the content area
 * fills available space using flex layout and enables vertical scrolling, keeping
 * the box header fixed while content scrolls independently. When [fill] is true, the
 * content area grows to fill available space without scrolling, so content can be
 * vertically centered against a stretched card height.
 */
@Composable
fun boxContent(
    padded: Boolean = true,
    scrollable: Boolean = false,
    fill: Boolean = false,
    content: @Composable () -> Unit = {}
) {
    Div({
        if (padded) {
            classes(BoxStyles.content)
        } else {
            classes(BoxStyles.noPaddingContent)
        }
        if (scrollable) {
            style {
                flex(1)
                minHeight(0.px)
                overflow("auto")
            }
        } else if (fill) {
            style {
                flex(1)
                minHeight(0.px)
            }
        }
    }) {
        content()
    }
}

/**
 * Render a message inside a box.
 */
@Composable
fun boxMessage(message: String) {
    boxContent {
        Div({ classes(BoxStyles.boxMessage) }) {
            Text(message)
        }
    }
}

@Composable
fun boxSpinner(message: String? = null) {
    boxContent {
        if (message != null) {
            Div({ classes(BoxStyles.boxMessage) }) {
                Div {
                    spinner()
                }
                Div {
                    Text(message)
                }
            }
        } else {
            spinner()
        }
    }
}

@Composable
fun <T> boxTable(title: String? = null, config: TableBuilder<T>.() -> Unit) {
    box({
        this.title = title
        paddedContent = false
    }) {
        Div({
            style { margin((-1).px) }
        }) {
            table(config)
        }
    }
}
